"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { ExternalLink, Loader2, ArrowUp, ArrowDown, ArrowUpDown, AlertCircle } from "lucide-react"
import type { NFTCollection } from "@/lib/api"
import { formatNumber } from "@/lib/utils"
import { getCubeImageForPreview } from "@/lib/seasonal-images"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { getOpenSeaUrl } from "@/lib/opensea-utils"
import { error } from "@/lib/debug"
import Image from "next/image"

interface CollectionGridProps {
  collections: NFTCollection[]
}

// Define sort types
type SortField = "name" | "floorPrice" | "addedAt"
type SortDirection = "asc" | "desc" | null

// Define floor price states
type FloorPriceState = {
  price: number | null
  currency: string
  isLoading: boolean
  error: string | null
  lastUpdated: number | null
}

export function CollectionGrid({ collections }: CollectionGridProps) {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({})
  const [floorPriceStates, setFloorPriceStates] = useState<Record<string, FloorPriceState>>({})
  const [isLoadingPrices, setIsLoadingPrices] = useState(true)

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("addedAt")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  // Cube size for images
  const cubeSize = 240

  // Add this function to fetch CryptoPunks floor price specifically
  const fetchCryptoPunksFloorPrice = async (): Promise<number | null> => {
    try {
      console.log("Fetching CryptoPunks floor price...")

      // Use a fallback value immediately to avoid cascading errors
      const fallbackPrice = 23.5

      try {
        const response = await fetch("/api/cryptopunks-floor", {
          next: { revalidate: 604800 },
        })

        if (!response.ok) {
          console.error(`Error fetching CryptoPunks floor price: ${response.status}`)
          return fallbackPrice // Return fallback value on error
        }

        const data = await response.json()

        if (data.success && data.stats && data.stats.floorPrice) {
          // Log the source of the floor price for debugging
          console.log(`CryptoPunks floor price source: ${data.stats.source || "unknown"}`)
          return data.stats.floorPrice
        }

        // If we have stats but no success flag, it might be our fallback
        if (!data.success && data.stats && data.stats.floorPrice) {
          console.log(
            `Using fallback CryptoPunks floor price: ${data.stats.floorPrice} from ${data.stats.source || "unknown"}`,
          )
          return data.stats.floorPrice
        }
      } catch (error) {
        console.error("Error in CryptoPunks floor price fetch:", error)
        // Continue to fallback
      }

      console.log("Using default fallback floor price")
      return fallbackPrice // Return fallback value if no price found or on error
    } catch (error) {
      console.error("Error in CryptoPunks floor price function:", error)
      return 23.5 // Return fallback value on any error
    }
  }

  // Fetch floor prices from our API
  useEffect(() => {
    const fetchFloorPrices = async () => {
      try {
        setIsLoadingPrices(true)

        // Initialize floor price states for all collections
        const initialStates: Record<string, FloorPriceState> = {}
        collections.forEach((collection) => {
          initialStates[collection.id] = {
            price: collection.floorPrice || null,
            isLoading: true,
            error: null,
            lastUpdated: null,
          }
        })
        setFloorPriceStates(initialStates)

        // Special handling for CryptoPunks
        try {
          const cryptoPunksCollection = collections.find((c) => c.id === "cryptopunks")
          if (cryptoPunksCollection) {
            const cryptoPunksFloorPrice = await fetchCryptoPunksFloorPrice()
            if (cryptoPunksFloorPrice !== null) {
              setFloorPriceStates((prev) => ({
                ...prev,
                cryptopunks: {
                  price: cryptoPunksFloorPrice,
                  currency: "ETH",
                  isLoading: false,
                  error: null,
                  lastUpdated: Date.now(),
                },
              }))
            }
          }
        } catch (cryptoPunksError) {
          console.error("Error in CryptoPunks special handling:", cryptoPunksError)
          // Continue with other collections
        }

        // Fetch prices from API for other collections
        try {
          const response = await fetch("/api/floor-prices", {
            // Use default caching - browser will cache for 1 week based on server headers
            next: { revalidate: 604800 }, // Revalidate every week
          })

          // Use fallback values if the API fails
          if (!response.ok) {
            console.error(`Error fetching floor prices: ${response.status}`)

            // Update all collections with their default floor prices
            const fallbackStates: Record<string, FloorPriceState> = {}
            collections.forEach((collection) => {
              // Skip CryptoPunks as we've already handled it
              if (collection.id === "cryptopunks") return

              fallbackStates[collection.id] = {
                price: collection.floorPrice || null,
                currency: collection.chain === "ronin" ? "RON" : "ETH",
                isLoading: false,
                error: "API error, using default price",
                lastUpdated: Date.now(),
              }
            })

            setFloorPriceStates((prev) => ({
              ...prev,
              ...fallbackStates,
            }))

            return // Exit early
          }

          const data = await response.json()
          const fetchedPrices = data.floorPrices || {}
          const fetchedCurrencies = data.currencies || {}

          // Update floor price states with fetched data
          const updatedStates: Record<string, FloorPriceState> = {}
          collections.forEach((collection) => {
            // Skip CryptoPunks as we've already handled it
            if (collection.id === "cryptopunks") return

            const fetchedPrice = fetchedPrices[collection.id]
            const currency = fetchedCurrencies[collection.id] || (collection.chain === "ronin" ? "RON" : "ETH")
            updatedStates[collection.id] = {
              price: fetchedPrice !== undefined ? fetchedPrice : null,
              currency: currency,
              isLoading: false,
              error: fetchedPrice === undefined ? "Price unavailable" : null,
              lastUpdated: fetchedPrice !== undefined ? Date.now() : null,
            }
          })

          setFloorPriceStates((prev) => ({
            ...prev,
            ...updatedStates,
          }))
        } catch (apiError) {
          console.error("Error fetching from floor prices API:", apiError)

          // Update all collections: no estimated fallback, show unavailable
          const errorStates: Record<string, FloorPriceState> = {}
          collections.forEach((collection) => {
            // Skip CryptoPunks as we've already handled it
            if (collection.id === "cryptopunks") return

            errorStates[collection.id] = {
              price: null,
              currency: collection.chain === "ronin" ? "RON" : "ETH",
              isLoading: false,
              error: "Failed to fetch price",
              lastUpdated: null,
            }
          })

          setFloorPriceStates((prev) => ({
            ...prev,
            ...errorStates,
          }))
        }
      } catch (error) {
        console.error("Error in fetchFloorPrices:", error)

        // Set error state for all collections
        const errorStates: Record<string, FloorPriceState> = {}
        collections.forEach((collection) => {
          errorStates[collection.id] = {
            price: null,
            isLoading: false,
            error: "Failed to fetch price",
            lastUpdated: null,
          }
        })
        setFloorPriceStates(errorStates)
      } finally {
        setIsLoadingPrices(false)
      }
    }

    fetchFloorPrices()

    // Set up periodic refresh (every week)
    const refreshInterval = setInterval(fetchFloorPrices, 7 * 24 * 60 * 60 * 1000)

    return () => clearInterval(refreshInterval)
  }, []) // Run only once on mount

  // Get image source with fallback paths
  const getImageSrc = (collection: NFTCollection) => {
    if (imageErrors[collection.id]) {
      // If we've already tried and failed to load the image, use a placeholder
      return `/placeholder.svg?height=${cubeSize}&width=${cubeSize}&query=${encodeURIComponent(collection.name + " NFT cube")}`
    }

    // Get seasonal image for preview if active, otherwise use default
    let originalPath = getCubeImageForPreview(collection.id)

    // If the path doesn't start with /images/ and doesn't have http, prepend /images/
    if (originalPath && !originalPath.startsWith("/images/") && !originalPath.startsWith("http")) {
      // Extract just the filename if it's a path
      const parts = originalPath.split("/")
      const fileName = parts[parts.length - 1]

      // Reconstruct with /images/ prefix
      originalPath = `/images/${fileName}`
    }

    // Ensure the path has an extension
    if (originalPath && !originalPath.match(/\.(webp|png|jpg|jpeg|gif|svg)$/i)) {
      originalPath = `${originalPath}.webp`
    }

    return (
      originalPath ||
      `/placeholder.svg?height=${cubeSize}&width=${cubeSize}&query=${encodeURIComponent(collection.name + " NFT cube")}`
    )
  }

  // Handle image error by tracking which images failed to load
  const handleImageError = (collectionId: string) => {
    error(`Failed to load image for collection: ${collectionId}`)

    // Try alternative formats if the original format fails
    const collection = collections.find((c) => c.id === collectionId)
    if (collection) {
      const currentPath = collection.cubeImage || ""

      // If we're already using a placeholder, don't try again
      if (currentPath.includes("placeholder.svg")) {
        setImageErrors((prev) => ({
          ...prev,
          [collectionId]: true,
        }))
        return
      }

      // Try different image formats
      const formats = ["webp", "png", "jpg", "jpeg"]
      const currentFormat = currentPath.split(".").pop()?.toLowerCase() || ""

      // Find the next format to try
      const nextFormatIndex = formats.indexOf(currentFormat) + 1
      if (nextFormatIndex < formats.length) {
        const nextFormat = formats[nextFormatIndex]
        const basePath = currentPath.substring(0, currentPath.lastIndexOf("."))
        const newPath = `${basePath}.${nextFormat}`

        console.log(`Trying alternative format: ${newPath}`)

        // Update the collection's image path
        collection.cubeImage = newPath

        // Reset the error state for this collection
        setImageErrors((prev) => ({
          ...prev,
          [collectionId]: false,
        }))

        return
      }

      // If all formats fail, try a different path structure
      if (!currentPath.includes("/images/")) {
        const fileName = currentPath.split("/").pop()?.split(".")[0]
        if (fileName) {
          const alternativePath = `/images/${fileName}.webp`
          console.log(`Trying alternative path structure: ${alternativePath}`)
          collection.cubeImage = alternativePath

          setImageErrors((prev) => ({
            ...prev,
            [collectionId]: false,
          }))
          return
        }
      }

      // All attempts failed, mark as error
      setImageErrors((prev) => ({
        ...prev,
        [collectionId]: true,
      }))
    } else {
      // Collection not found, mark as error
      setImageErrors((prev) => ({
        ...prev,
        [collectionId]: true,
      }))
    }
  }

  // Use the shared OpenSea URL utility
  function getCollectionOpenSeaUrl(collection: NFTCollection): string {
    // Simply use the utility function that now properly handles all cases
    return getOpenSeaUrl(collection)
  }

  // Get the floor price state for a collection
  const getFloorPriceState = (collection: NFTCollection): FloorPriceState => {
    return (
      floorPriceStates[collection.id] || {
        price: collection.floorPrice || null,
        isLoading: isLoadingPrices,
        error: null,
        lastUpdated: null,
      }
    )
  }

  // Format the last updated time
  const formatLastUpdated = (timestamp: number | null): string => {
    if (!timestamp) return "Never"

    const now = Date.now()
    const diffSeconds = Math.floor((now - timestamp) / 1000)

    if (diffSeconds < 60) return "Just now"
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`
    return `${Math.floor(diffSeconds / 86400)}d ago`
  }

  // Add a function to format the date in a readable format
  // Add this function after the formatLastUpdated function:

  // Format the addition date in a readable format
  const formatAddedDate = (timestamp: number | undefined): string => {
    if (!timestamp) return "Unknown date"

    const date = new Date(timestamp)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  // Add a function to check if a collection was recently added
  // Add this function after the formatAddedDate function:

  // Check if a collection was added recently (within the last 3 days)
  const isRecentlyAdded = (timestamp: number | undefined): boolean => {
    if (!timestamp) return false

    const threeDaysAgo = Date.now() - 3 * 24 * 60 * 60 * 1000
    return timestamp > threeDaysAgo
  }

  // Handle sort button click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field is clicked
      if (sortDirection === "asc") {
        setSortDirection("desc")
      } else if (sortDirection === "desc") {
        // Reset sorting if already descending
        setSortField(null)
        setSortDirection(null)
      } else {
        setSortDirection("asc")
      }
    } else {
      // Set new field and default to ascending
      setSortField(field)
      setSortDirection("asc")
    }
  }

  // Get sort icon based on current state
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={16} />
    }
    return sortDirection === "asc" ? <ArrowUp size={16} /> : <ArrowDown size={16} />
  }

  // Sort collections based on current sort settings and ensure uniqueness by ID
  const sortedCollections = [...collections]
    // First, filter out duplicates by keeping only the first occurrence of each collection ID
    .filter((collection, index, self) => index === self.findIndex((c) => c.id === collection.id))
    // Then apply the sorting logic
    .sort((a, b) => {
      if (!sortField || !sortDirection) return 0

      if (sortField === "name") {
        return sortDirection === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      }

      if (sortField === "floorPrice") {
        const stateA = getFloorPriceState(a)
        const stateB = getFloorPriceState(b)
        const priceA = stateA.price || 0
        const priceB = stateB.price || 0
        return sortDirection === "asc" ? priceA - priceB : priceB - priceA
      }

      if (sortField === "addedAt") {
        const dateA = a.addedAt || 0
        const dateB = b.addedAt || 0
        return sortDirection === "asc" ? dateA - dateB : dateB - dateA
      }

      return 0
    })

  return (
    <TooltipProvider>
      <div>
        {/* Sort controls */}
        <div className="flex gap-2 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSort("name")}
            className={`flex items-center gap-1 ${sortField === "name" ? "bg-gray-100" : ""}`}
          >
            Name {getSortIcon("name")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSort("floorPrice")}
            className={`flex items-center gap-1 ${sortField === "floorPrice" ? "bg-gray-100" : ""}`}
          >
            Floor Price {getSortIcon("floorPrice")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSort("addedAt")}
            className={`flex items-center gap-1 ${sortField === "addedAt" ? "bg-gray-100" : ""}`}
          >
            Recently Added {getSortIcon("addedAt")}
          </Button>
        </div>

        {/* Collection grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 sm:gap-6 md:gap-8 pb-6">
          {sortedCollections.map((collection) => {
            // Get image with fallbacks
            const imageSrc = getImageSrc(collection)

            // Get floor price state
            const floorPriceState = getFloorPriceState(collection)

            // Get OpenSea URL
            const openSeaUrl = getCollectionOpenSeaUrl(collection)

            return (
              <motion.div
                key={collection.id}
                className="bg-transparent overflow-hidden group relative"
                whileHover="hover"
                initial="rest"
                animate="rest"
                variants={{
                  rest: { y: 0 },
                  hover: { y: 0 }, // Container doesn't move, only the contents will animate
                }}
              >
                <a href={openSeaUrl} target="_blank" rel="noopener noreferrer" className="block h-full">
                  <div className="flex flex-col h-full">
                    <div className="relative aspect-square mb-4 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center">
                      {/* Shadow element - hidden until hover */}
                      <motion.div
                        className="absolute bottom-[15%] w-[50%] h-[10%] bg-gray-400 rounded-[50%] blur-md z-0"
                        variants={{
                          rest: {
                            width: "50%",
                            opacity: 0, // Completely hidden in rest state
                          },
                          hover: {
                            width: ["50%", "60%", "50%"],
                            opacity: [0.3, 0.5, 0.3], // Becomes visible and animates during hover
                            transition: {
                              duration: 1.5,
                              repeat: Number.POSITIVE_INFINITY,
                              repeatType: "loop",
                              ease: "easeInOut",
                              times: [0, 0.5, 1],
                            },
                          },
                        }}
                      />

                      {/* Image container with levitation animation */}
                      <motion.div
                        className="w-[75%] h-[75%] relative z-10"
                        variants={{
                          rest: { y: 0 },
                          hover: {
                            y: [0, -10, 0],
                            transition: {
                              duration: 1.5,
                              repeat: Number.POSITIVE_INFINITY,
                              repeatType: "loop",
                              ease: "easeInOut",
                              times: [0, 0.5, 1],
                            },
                          },
                        }}
                      >
                        <Image
                          src={imageSrc || "/placeholder.svg"}
                          alt={collection.name}
                          fill
                          className="object-contain"
                          onError={() => handleImageError(collection.id)}
                          // Cache hint: immutable assets in /public/images are long-lived
                          // Next.js Image doesn't expose Cache-Control per image on client,
                          // but static assets under /public are cached aggressively by Vercel CDN.
                          // We keep this comment to note intent.
                        />
                      </motion.div>
                      {/* Chain indicator */}
                      {collection.chain && collection.chain !== "ethereum" && (
                        <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                          {collection.chain === "base"
                            ? "Base"
                            : collection.chain === "ronin"
                              ? "Ronin"
                              : collection.chain}
                        </div>
                      )}
                      {isRecentlyAdded(collection.addedAt) && (
                        <div className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full font-bold">
                          New
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <h3
                            className="text-lg font-semibold text-gray-900 font-funnel truncate overflow-hidden whitespace-nowrap"
                            title={collection.name}
                          >
                            {collection.name}
                          </h3>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Added: {formatAddedDate(collection.addedAt)}</p>
                        </TooltipContent>
                      </Tooltip>

                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                          {floorPriceState.isLoading ? (
                            <div className="flex items-center">
                              <Loader2 size={14} className="animate-spin text-gray-400" />
                            </div>
                          ) : floorPriceState.error ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex items-center text-amber-600">
                                  <AlertCircle size={14} className="mr-1" />
                                  {"Price unavailable"}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Error: {floorPriceState.error}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : floorPriceState.price ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>Floor: {formatNumber(floorPriceState.price)} {floorPriceState.currency}</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Last updated: {formatLastUpdated(floorPriceState.lastUpdated)}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span>Floor price unavailable</span>
                          )}
                        </div>

                        <div className="text-brand-purple opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <ExternalLink size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </motion.div>
            )
          })}
        </div>
      </div>
    </TooltipProvider>
  )
}
